import { useEffect, useMemo, useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { api, formatApiError } from "../lib/api";
import { ALL_SERVICES } from "../lib/site";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const Stars = ({ value, size = 14 }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} fill={i <= value ? "currentColor" : "none"} className={i <= value ? "text-primary" : "text-neutral-700"} />
    ))}
  </span>
);

export const Reviews = () => {
  const [reviews, setReviews] = useState(null);
  const [form, setForm] = useState({ customer_name: "", rating: 5, comment: "", service_id: "none" });
  const [hoverStar, setHoverStar] = useState(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/reviews").then(({ data }) => setReviews(data)).catch(() => setReviews([]));
  }, []);

  const avg = useMemo(() => {
    if (!reviews || reviews.length === 0) return "5.0";
    return (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1);
  }, [reviews]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/reviews", {
        customer_name: form.customer_name,
        rating: form.rating,
        comment: form.comment,
        service_id: form.service_id === "none" ? null : form.service_id,
      });
      setReviews((rs) => [data, ...(rs || [])]);
      setForm({ customer_name: "", rating: 5, comment: "", service_id: "none" });
      toast.success("Thanks! Your review is live.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 md:py-32 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <p className="eyebrow mb-4 text-center">04 — Reviews</p>
          <div className="text-center">
            <h2 className="font-display font-bold uppercase tracking-normal text-4xl sm:text-5xl lg:text-6xl max-w-2xl mx-auto leading-[1.02]">
              <span className="block">Minneapolis keeps</span>
              <span className="block">calling back.</span>
            </h2>
            <div className="mt-6 inline-flex items-center gap-4" data-testid="reviews-average">
              <span className="font-display font-bold text-6xl text-primary">{avg}</span>
              <div>
                <Stars value={Math.round(Number(avg))} size={18} />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-2">
                  {(reviews || []).length} verified reviews
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-[1fr_1.5fr] gap-10 items-start">
          <Reveal>
            <form data-testid="review-form" onSubmit={submit} className="border border-white/10 bg-[#121212] p-6 md:p-8 space-y-5 lg:sticky lg:top-24">
              <h3 className="font-display font-semibold uppercase tracking-normal text-2xl">Leave a review</h3>
              <p className="text-sm text-neutral-400">We helped you out? Tell the next stranded driver.</p>

              <label className="block">
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">Your Name</span>
                <input data-testid="review-name-input" required value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} className="field-input" placeholder="Alex J." />
              </label>

              <div>
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">Rating</span>
                <div className="flex gap-1" data-testid="review-stars">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`review-star-${i}`}
                      onMouseEnter={() => setHoverStar(i)}
                      onMouseLeave={() => setHoverStar(0)}
                      onClick={() => setForm((f) => ({ ...f, rating: i }))}
                      className="p-1 transition-transform duration-150 hover:scale-125"
                      aria-label={`${i} star${i > 1 ? "s" : ""}`}
                    >
                      <Star size={26} fill={i <= (hoverStar || form.rating) ? "currentColor" : "none"} className={i <= (hoverStar || form.rating) ? "text-primary" : "text-neutral-600"} />
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">Service We Provided (optional)</span>
                <Select value={form.service_id} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v }))}>
                  <SelectTrigger data-testid="review-service-select" className="field-input !py-3 h-auto rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#333] text-white rounded-none max-h-72">
                    <SelectItem value="none" data-testid="review-service-none">General / not listed</SelectItem>
                    {ALL_SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`review-service-${s.id}`}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>

              <label className="block">
                <span className="block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">Your Review</span>
                <textarea
                  data-testid="review-comment-input"
                  required
                  rows={4}
                  maxLength={600}
                  value={form.comment}
                  onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                  className="field-input resize-none"
                  placeholder="How'd we do?"
                />
              </label>

              <button data-testid="review-submit-btn" type="submit" disabled={sending} className="btn-primary w-full disabled:opacity-60 disabled:pointer-events-none">
                {sending ? "Posting…" : "Post Review"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="grid sm:grid-cols-2 gap-4" data-testid="reviews-list">
              {reviews === null ? (
                <p className="text-neutral-500 font-mono text-xs py-10">Loading reviews…</p>
              ) : reviews.length === 0 ? (
                <p className="text-neutral-500 font-mono text-xs py-10">No reviews yet — be the first.</p>
              ) : (
                reviews.map((r) => (
                  <article key={r.id} data-testid={`review-card-${r.id}`} className="border border-white/10 bg-[#0D0D0D] p-5 transition-colors duration-300 hover:border-primary/40">
                    <Stars value={r.rating} />
                    <p className="mt-3 text-sm text-neutral-300 leading-relaxed">"{r.comment}"</p>
                    <div className="mt-4 flex items-baseline justify-between gap-3">
                      <span className="font-medium text-sm">{r.customer_name}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500 text-right">
                        {r.service_label || "Roadside service"}
                      </span>
                    </div>
                  </article>
                ))
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
